/**
 * Shared scheduler for SYNC HTTP calls (sync request-storm fix, P0-a).
 *
 * All read-like sync requests (the /umbra/v2/sync/* + chat-list calls, and later
 * the orchestrator message refetch) route through `scheduleSyncRequest`. It
 * provides three things that together stop the 429 cascade and the thundering
 * herd it would otherwise create:
 *
 *  1. A client-side TOKEN BUCKET (burst + sustained cap) that paces dispatch
 *     below the BE's per-user sync/read buckets so we mostly never hit a 429 in
 *     the first place. Initial values are conservative; tune against a beta-om
 *     measurement (plan unknown U3).
 *  2. A SHARED 429 COOLDOWN: when any sync call gets a 429, every sync call
 *     waits out the server-specified delay. Acquisition is SERIALIZED through a
 *     single promise chain and the bucket is drained on cooldown, so workers
 *     reacquire ONE AT A TIME after the cooldown instead of all firing on the
 *     same tick (the herd that bounded-retry-without-a-shared-cooldown creates).
 *  3. BOUNDED auto-retry (≤ MAX_RETRIES_PER_CALL, capped total wait). Safe only
 *     because the sync calls are idempotent reads — NEVER wire writes through
 *     this. On a non-429 error it rethrows that error unchanged; on a rate-limit
 *     give-up (retries exhausted / deadline) it throws `SyncSchedulerTimeout` so
 *     callers can treat "we're throttled" as a run-fatal signal (hold cursors,
 *     resume next trigger) — distinct from a per-item failure. It ALWAYS engages
 *     the shared cooldown for any parsed 429, including on the give-up branch,
 *     so other in-flight sync workers don't herd the just-throttled server.
 *
 * Kept separate from the axios 401 token-refresh queue (apiService.ts): the two
 * concerns don't interact — this wraps the call, the refresh interceptor lives
 * inside the axios client.
 */

import { AxiosError } from "axios";
import { createLogger } from "@/utils/debug";
import { isSyncRunningInAnotherTab } from "./syncLock";

const log = createLogger("SyncScheduler");

// --- Tuning (initial; revisit with a beta-om measurement — plan U3) ----------
// Held in a mutable config so tests can inject fast timing via
// `_resetSyncScheduler(overrides)`; production always uses these defaults.
interface SchedulerConfig {
  /** Burst allowance — requests that may fire back-to-back before throttling. */
  bucketCapacity: number;
  /** Sustained refill rate (tokens/ms), kept below the BE per-user buckets. */
  refillPerMs: number;
  /** Bounded auto-retry on 429. */
  maxRetriesPerCall: number;
  /** Hard cap on total time a single call may spend waiting out cooldowns. */
  maxTotalWaitMs: number;
  /** Used when a 429 carries no parseable delay header. */
  cooldownFallbackMs: number;
  /** Random extra wait added to a cooldown so workers don't align exactly. */
  maxJitterMs: number;
}

const DEFAULT_CONFIG: SchedulerConfig = {
  bucketCapacity: 15,
  refillPerMs: 45 / 60_000, // ~45 req/min
  maxRetriesPerCall: 3,
  maxTotalWaitMs: 60_000,
  cooldownFallbackMs: 5_000,
  maxJitterMs: 500,
};

let config: SchedulerConfig = { ...DEFAULT_CONFIG };

// --- Shared state -------------------------------------------------------------
let tokens = config.bucketCapacity;
let lastRefillAt = 0; // lazily initialized on first acquire (no Date.now at import)
let cooldownUntil = 0;
let chain: Promise<void> = Promise.resolve();

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

function refill(now: number): void {
  if (lastRefillAt === 0) {
    lastRefillAt = now;
    return;
  }
  const elapsed = now - lastRefillAt;
  if (elapsed <= 0) {
    return;
  }
  tokens = Math.min(
    config.bucketCapacity,
    tokens + elapsed * config.refillPerMs,
  );
  lastRefillAt = now;
}

/**
 * Engage the shared cooldown after a 429. Drains the bucket and pins the refill
 * clock to the cooldown's end so that, once it lifts, tokens regenerate from
 * zero — serialized acquirers then reacquire one at a time (no herd) rather than
 * all draining a full burst on the same tick.
 */
function engageCooldown(ms: number): void {
  const until = Date.now() + ms;
  cooldownUntil = Math.max(cooldownUntil, until);
  tokens = 0;
  lastRefillAt = cooldownUntil;
}

/**
 * Parse a retry delay (ms) from a 429 response's headers. Handles BOTH dialects
 * the BE can emit (see plan U2): legacy `Retry-After` + `X-RateLimit-Reset`
 * (syncRateLimiter, default), and IETF draft-6 `RateLimit-Reset` (the global
 * middleware on prod). `Retry-After` and `RateLimit-Reset` are delta-seconds;
 * `X-RateLimit-Reset` is an epoch-seconds timestamp. Returns null if none parse,
 * so the caller can fall back to a fixed delay.
 */
export function parseRetryDelayMs(
  headers: Record<string, unknown> | undefined,
): number | null {
  if (!headers) {
    return null;
  }
  const num = (key: string): number | null => {
    const raw = headers[key];
    if (raw == null) {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  // 1. Retry-After — delta seconds OR an HTTP-date (RFC 7231 allows both).
  const retryAfterRaw = headers["retry-after"];
  if (retryAfterRaw != null) {
    const seconds = Number(retryAfterRaw);
    if (Number.isFinite(seconds)) {
      return Math.max(0, seconds * 1000);
    }
    const dateMs = Date.parse(String(retryAfterRaw));
    if (Number.isFinite(dateMs)) {
      return Math.max(0, dateMs - Date.now());
    }
  }
  // 2. draft-6 RateLimit-Reset — delta seconds.
  const rlReset = num("ratelimit-reset");
  if (rlReset != null) {
    return Math.max(0, rlReset * 1000);
  }
  // 3. legacy X-RateLimit-Reset — epoch seconds → delta from now.
  const xReset = num("x-ratelimit-reset");
  if (xReset != null) {
    return Math.max(0, xReset * 1000 - Date.now());
  }
  return null;
}

/** Extract a 429 retry delay from a thrown error, or null if it isn't a 429. */
function retryDelayFromError(error: unknown): number | null {
  if (!(error instanceof AxiosError) || error.response?.status !== 429) {
    return null;
  }
  const parsed = parseRetryDelayMs(
    error.response.headers as Record<string, unknown> | undefined,
  );
  const base = parsed ?? config.cooldownFallbackMs;
  return base + Math.floor(Math.random() * config.maxJitterMs);
}

/** Thrown when a queued sync call can't acquire a slot within its budget. */
export class SyncSchedulerTimeout extends Error {
  constructor() {
    super("sync request exceeded its rate-limit wait budget");
    this.name = "SyncSchedulerTimeout";
  }
}

// --- Chat-list reconciliation gate (cold-start non-starvation, Phase 1) -------
//
// THE incident fix. On cold start / SW update the chat-list reconciliation
// (`paginateChatList` → the full or delta chat-list traversal) competes for the
// SAME shared token bucket as every other sync consumer. The heaviest of those —
// the decryption-retry orchestrator's legacy per-message `getMessage` refetch —
// fires on the SAME socket-connect event that triggers `runSync`, so its calls
// drain the bucket (and trip the shared 429 cooldown) WHILE the chat-list
// paginator is mid-traversal. The paginator then waits out the cooldown, blows
// its 60s deadline, throws `SyncSchedulerTimeout`, and aborts — stranding the
// tail (older + AI/`hubId` chats) indefinitely (the cursor only seeds on a
// COMPLETE traversal, so the next run restarts from page 1 and re-hits the wall).
//
// Fix = SEQUENCING, not lanes/priority tokens: while a chat-list reconciliation
// is in progress, NON-ESSENTIAL background consumers (the orchestrator's network
// refetch, and any future background pass) must YIELD — defer their sync calls
// until the reconciliation completes — so the reconciliation owns the budget and
// is NON-STARVABLE. The gate is raised at the TRIGGER SITE (`useSync`'s
// `triggerSync`) SYNCHRONOUSLY, before the `await withSyncLock(() => runSync())`
// hop — NOT as the first statement of `runSync` (which only runs AFTER several
// awaits, inside `withSyncLock` → `navigator.locks.request`). That ordering
// matters: the orchestrator's deferral fires on the SAME socket-connect event,
// so if the gate were only raised inside `runSync` it could observe depth 0 and
// slip a `getMessage` through before the gate went up. Raised at the trigger, the
// gate is provably up before any async hop on every runSync path. The chokepoint
// `begin` does NOT await the gate, and the reconciliation itself never waits on a
// background consumer, so this gate cannot deadlock the reconciliation.
//
// Reference-counted (a nested/overlapping reconciliation, or a tab firing two
// runs, won't release the gate early) and exposed as a promise so a waiter wakes
// the instant the gate clears rather than polling.
//
// Hard cap on how long a background consumer will defer for a reconciliation.
//
// This is a LAST-RESORT safety valve, NOT the normal release path: the trigger
// site (`useSync`'s `triggerSync`) always balances begin/end in a `try/finally`
// around the whole `withSyncLock(() => runSync())` call, so the ONLY way the gate
// stays up is a hard crash that kills the run mid-flight (in which case the page
// is gone too) — or a programming error that leaks a `begin`. The cap must
// therefore sit COMFORTABLY ABOVE the worst-case
// legitimate full traversal — otherwise it would release the deferred background
// refetches WHILE a heavy account is still paginating its tail, re-opening the
// very starvation this fix closes (Codex review). Worst case today: ~200 chat-
// list pages (100 group + 100 P2P) at the scheduler's ~45 req/min sustained rate
// ≈ 4.5 min, before any 429 cooldowns extend it. 10 min leaves generous headroom
// so the valve never fires on a real traversal, while a genuinely leaked gate
// (programming error / crash) still can't pin a consumer for the whole session.
const RECONCILIATION_WAIT_CAP_MS = 10 * 60_000;

let reconciliationDepth = 0;
let reconciliationDone: Promise<void> = Promise.resolve();
let resolveReconciliation: (() => void) | null = null;
// Monotonic generation counter, bumped on EVERY `begin` (not just depth 0 → 1).
// The safety-valve timeout in `awaitChatListReconciliationIdle` captures the
// generation it is waiting on and only force-releases the gate if that SAME
// generation is still current when the timer fires — i.e. NO `begin` has run
// since the waiter armed. Bumping on every begin (rather than only on the fresh
// 0 → 1 transition) is what makes the guard correct under DEPTH > 1: a second,
// overlapping reconciliation (depth 1 → 2) is also a live holder of the gate, so
// it too must advance the generation and thereby protect itself from a stale
// waiter's force-release. A genuinely leaked gate (a `begin` with no further
// begin/end activity) leaves the generation unchanged → the valve fires.
let reconciliationGeneration = 0;

/**
 * Mark the start of a chat-list reconciliation. Background consumers that call
 * {@link awaitChatListReconciliationIdle} will defer until the matching
 * {@link endChatListReconciliation}. Reference-counted: balance every call.
 */
export function beginChatListReconciliation(): void {
  reconciliationDepth += 1;
  // Bump on EVERY begin so an overlapping reconciliation (depth > 1) is also
  // protected from a stale waiter's safety-valve force-release (see above).
  reconciliationGeneration += 1;
  if (reconciliationDepth === 1) {
    reconciliationDone = new Promise<void>((resolve) => {
      resolveReconciliation = resolve;
    });
  }
}

/**
 * Mark the end of a chat-list reconciliation (balance {@link
 * beginChatListReconciliation}). When the last in-flight reconciliation ends,
 * any deferred background consumers are released. Idempotent below zero.
 */
export function endChatListReconciliation(): void {
  if (reconciliationDepth === 0) {
    return;
  }
  reconciliationDepth -= 1;
  if (reconciliationDepth === 0) {
    resolveReconciliation?.();
    resolveReconciliation = null;
    reconciliationDone = Promise.resolve();
  }
}

/** True while a chat-list reconciliation holds the gate. */
export function isChatListReconciliationInProgress(): boolean {
  return reconciliationDepth > 0;
}

/**
 * Force the gate fully open regardless of depth — the safety valve's recovery
 * action for a LEAKED gate (a `begin` whose `end` never ran). Resets depth to 0,
 * resolves any deferred waiters, and clears the promise so a future
 * `awaitChatListReconciliationIdle` returns immediately. Unlike
 * {@link endChatListReconciliation} (which only decrements), this clears the
 * WHOLE ref-count, because a leaked gate's true depth is unknown/unbalanced.
 */
function forceReleaseReconciliation(): void {
  reconciliationDepth = 0;
  resolveReconciliation?.();
  resolveReconciliation = null;
  reconciliationDone = Promise.resolve();
}

/**
 * Resolves once no chat-list reconciliation is in progress. Background/
 * non-essential sync consumers (e.g. the decryption-retry orchestrator's legacy
 * `getMessage` refetch) `await` this BEFORE issuing their sync calls, so they
 * never starve the cold-start chat-list traversal of the shared rate budget.
 *
 * Returns immediately when no reconciliation is running. The essential
 * reconciliation path itself must NOT call this (it would self-deadlock); only
 * deferrable background consumers do.
 *
 * `timeoutMs` is a SAFETY VALVE, not the normal path: a reconciliation that
 * crashed without balancing its `begin`/`end` (or one that legitimately runs
 * very long) must never pin a background consumer forever. On timeout it BOTH
 * resolves the waiter AND force-releases the stale gate (resets depth → 0, marks
 * done) — so a genuinely leaked gate does NOT stay "in progress" forever making
 * every future refetch arm a fresh full-length timer (a permanent per-refetch
 * penalty). After the force-release the gate degrades cleanly to the old shared-
 * budget behavior, exactly as the surrounding comments claim.
 *
 * The force-release is guarded by a monotonic GENERATION counter: the waiter
 * records the generation it is blocked on, and only force-releases if that SAME
 * generation is still up when the timer fires. If the stale gate was already
 * released and a NEWER, legitimate reconciliation started in the meantime (a new
 * generation), the timeout resolves the waiter but leaves the new reconciliation
 * untouched — it must not clobber a live, correctly-balanced run.
 */
export function awaitChatListReconciliationIdle(
  timeoutMs = RECONCILIATION_WAIT_CAP_MS,
): Promise<void> {
  if (reconciliationDepth === 0) {
    return Promise.resolve();
  }
  const awaitedGeneration = reconciliationGeneration;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      // Safety valve fired. Force-release ONLY the gate we were waiting on: if
      // the generation still matches, NO begin has run since we armed — this is
      // the same (genuinely leaked) reconciliation, so clear it so it can't pin
      // future refetches. If ANY begin ran since (a newer reconciliation, OR an
      // overlapping one that raised depth > 1), the generation advanced — leave
      // it alone, don't clobber a live run.
      if (
        reconciliationDepth > 0 &&
        reconciliationGeneration === awaitedGeneration
      ) {
        forceReleaseReconciliation();
      }
      resolve();
    }, timeoutMs);
  });
  return Promise.race([reconciliationDone, timeout]).finally(() => {
    if (timer !== undefined) {
      clearTimeout(timer);
    }
  });
}

/** Poll interval (ms) while deferring for a reconciliation running in another tab. */
const CROSS_TAB_POLL_INTERVAL_MS = 250;

/**
 * Cross-tab-aware deferral for non-essential background sync consumers (#958).
 *
 * The per-tab gate (`awaitChatListReconciliationIdle`) only defers for a
 * reconciliation in THIS tab. With 2+ tabs, a lock-skipped tab's gate is down
 * while ANOTHER tab is mid-traversal, so this tab's background `getMessage`
 * refetch would still 429-starve the other tab's chat-list traversal. This
 * helper adds the cross-tab layer:
 *   1. Wait out THIS tab's own reconciliation (the unchanged same-tab fast path
 *      + generation-guarded safety valve).
 *   2. Then, while a sync/reconciliation is observably running in ANOTHER tab
 *      (`isSyncRunningInAnotherTab`, a read-only `navigator.locks.query()`),
 *      defer in short polls — bounded by the SAME `RECONCILIATION_WAIT_CAP_MS`
 *      safety valve, after which we proceed (degrade to the shared budget,
 *      exactly the existing valve semantics). The browser auto-releases the
 *      other tab's sync lock on its death, so this loop is self-terminating and
 *      can NEVER permanently block (no stale lease).
 *
 * The probe excludes THIS tab's own lock by `clientId`, so a single tab never
 * defers on itself (no added single-tab latency). When `navigator.locks` is
 * unavailable the probe returns false immediately → behavior == today.
 *
 * NOTE on the residual: a synchronously-observable cross-tab signal is
 * impossible with Web platform primitives (BroadcastChannel/IndexedDB/Web Locks
 * are all observed asynchronously cross-tab), so this delivers STEADY-STATE
 * deferral, not a hard cross-tab mutex. A few single refetches can still leak in
 * a microtask-width window (before the holder's `request()` is visible, or a
 * probe→fetch TOCTOU); each subsequent poll defers, so they cannot reproduce the
 * sustained FLOOD that caused the incident. See plan §"Accepted bounded leakage".
 */
export async function awaitReconciliationIdleCrossTab(
  timeoutMs = RECONCILIATION_WAIT_CAP_MS,
): Promise<void> {
  // ONE end-to-end deadline across BOTH phases — so the TOTAL deferral can never
  // exceed `timeoutMs`. A per-phase deadline would let the same-tab gate and the
  // cross-tab loop stack to ~2× the cap.
  const deadline = Date.now() + timeoutMs;

  // 1. Same-tab gate (unchanged): also resolves immediately + fully in single-tab.
  await awaitChatListReconciliationIdle(Math.max(0, deadline - Date.now()));

  // 2. Cross-tab: defer while another tab is reconciling, bounded by the valve.
  while (Date.now() < deadline) {
    let running: boolean;
    try {
      // Cap the one-time self-id derivation at the remaining budget so this probe
      // can never push the total deferral past `deadline` (the asserted invariant).
      running = await isSyncRunningInAnotherTab(deadline - Date.now());
    } catch {
      // Probe failure must never block a background consumer — proceed.
      return;
    }
    if (!running) {
      return;
    }
    const remaining = deadline - Date.now();
    if (remaining <= 0) {
      return;
    }
    await delay(Math.min(CROSS_TAB_POLL_INTERVAL_MS, remaining));
  }
}

/**
 * Acquire one dispatch slot: serialized, cooldown-aware, rate-limited. Bails
 * with {@link SyncSchedulerTimeout} once `deadline` passes — so a sustained 429
 * stream from OTHER workers (each extending the shared cooldown) can't pin a
 * queued call here past its `maxTotalWaitMs` budget.
 */
function acquireSlot(deadline: number): Promise<void> {
  const run = chain.then(async () => {
    for (;;) {
      const now = Date.now();
      if (now >= deadline) {
        throw new SyncSchedulerTimeout();
      }
      if (now < cooldownUntil) {
        // Sleep at most until the deadline, then re-check (the cooldown may
        // have been extended by another worker's 429 in the meantime).
        await delay(Math.min(cooldownUntil - now, deadline - now));
        continue;
      }
      refill(now);
      if (tokens >= 1) {
        tokens -= 1;
        return;
      }
      // Wait until ~1 token has regenerated (capped at the deadline).
      const tokenWait = Math.ceil((1 - tokens) / config.refillPerMs);
      await delay(Math.min(tokenWait, deadline - now));
    }
  });
  // The chain must keep flowing even if this acquire bailed (timeout) or a
  // prior one rejected — the next acquirer must still get its turn.
  chain = run.catch(() => undefined);
  return run;
}

/**
 * Run an idempotent sync request through the shared scheduler. Honors 429 with a
 * shared cooldown + bounded retry; rethrows on a non-429 error or once retries
 * are exhausted (the caller's cursor only advances on success).
 *
 * ONLY for read-like sync calls — never sends/edits/uploads.
 */
export async function scheduleSyncRequest<T>(fn: () => Promise<T>): Promise<T> {
  const deadline = Date.now() + config.maxTotalWaitMs;
  for (let attempt = 0; ; attempt++) {
    await acquireSlot(deadline);
    try {
      return await fn();
    } catch (error) {
      const retryMs = retryDelayFromError(error);
      if (retryMs == null) {
        // Not a 429 — a real error (network/4xx/5xx). Pass it through unchanged
        // so the caller sees the genuine failure.
        throw error;
      }
      // ANY parsed 429 must quiet ALL sync traffic — engage the shared cooldown
      // even if THIS caller is about to give up. Otherwise the other in-flight
      // sync workers (the 5-way message-backfill, the events paginator) would
      // dispatch straight into a server that just issued a back-off = a residual
      // herd on the worst-case window.
      engageCooldown(retryMs);
      if (
        attempt >= config.maxRetriesPerCall ||
        Date.now() + retryMs > deadline
      ) {
        // Giving up because of rate limiting (retries exhausted, or the cooldown
        // would overshoot the deadline). Throw a SINGLE identifiable error — the
        // same type `acquireSlot` throws on a deadline-in-queue — so callers can
        // treat "the scheduler is rate-limited" as a run-fatal signal (hold the
        // cursors, resume next trigger) rather than mistaking it for a per-item
        // failure and aging it through a per-item retry cap (which would advance
        // the cursor past un-synced data — see syncNewMessagesForChats).
        throw new SyncSchedulerTimeout();
      }
      log.warn("[sync] 429 — engaging shared cooldown", {
        retryMs,
        attempt: attempt + 1,
      });
    }
  }
}

/**
 * Read-only snapshot of the shared scheduler's contention state, for telemetry
 * ONLY (sync-telemetry `scheduler_timeout` event). Carries no PII — just the
 * token-bucket / cooldown counters that show whether a heavy account is being
 * starved by the rate limiter. Cheap and side-effect-free: it refills against
 * `Date.now()` so `tokensAvailable` reflects the current instant, but does NOT
 * consume a token, mutate the cooldown, or touch the acquire chain.
 */
export interface SchedulerSnapshot {
  /** Whole tokens currently available in the burst bucket (floored). */
  tokensAvailable: number;
  /** Burst capacity, for context (how starved `tokensAvailable` really is). */
  bucketCapacity: number;
  /** ms until the shared 429 cooldown lifts (0 when not cooling down). */
  cooldownRemainingMs: number;
  /** True while a shared 429 cooldown is in effect. */
  inCooldown: boolean;
}

export function getSchedulerSnapshot(): SchedulerSnapshot {
  const now = Date.now();
  // Mirror refill() WITHOUT mutating module state — telemetry must not perturb
  // the live scheduler. Compute the would-be token level at `now`.
  let projected = tokens;
  if (lastRefillAt !== 0) {
    const elapsed = now - lastRefillAt;
    if (elapsed > 0) {
      projected = Math.min(
        config.bucketCapacity,
        tokens + elapsed * config.refillPerMs,
      );
    }
  }
  const cooldownRemainingMs = Math.max(0, cooldownUntil - now);
  return {
    tokensAvailable: Math.floor(projected),
    bucketCapacity: config.bucketCapacity,
    cooldownRemainingMs,
    inCooldown: cooldownRemainingMs > 0,
  };
}

/**
 * Test-only: reset scheduler state between cases, optionally overriding the
 * tuning (e.g. an instant refill so a test observes only the 429 cooldown).
 */
export function _resetSyncScheduler(
  overrides?: Partial<SchedulerConfig>,
): void {
  config = { ...DEFAULT_CONFIG, ...overrides };
  tokens = config.bucketCapacity;
  lastRefillAt = 0;
  cooldownUntil = 0;
  chain = Promise.resolve();
  reconciliationDepth = 0;
  reconciliationGeneration = 0;
  resolveReconciliation?.();
  resolveReconciliation = null;
  reconciliationDone = Promise.resolve();
}
